import { Injectable } from "@angular/core";
import Dexie, { Table, DBCore, Middleware } from "dexie";
import { DataService } from "./data.service";
import { BehaviorSubject } from "rxjs";

// ===================================================================
//  1. DEFINE THE QUEUE DATABASE (New, Simple, and Separate)
// ===================================================================

// An interface for the data you'll store in the queue
export interface IQueuedRequest {
  id?: number;
  timestamp: number;
  payload: any;
}

// A new, lightweight Dexie class just for the queue
class QueueDB extends Dexie {
  requests: Table<IQueuedRequest, number>;

  constructor() {
    // This database will always be named 'qservice'
    super("qservice");
    this.version(1).stores({
      requests: "++id, timestamp",
    });
    this.requests = this.table("requests");
  }
}

// Export a single instance of the queue database
export const qService = new QueueDB();

// ===================================================================
//  2. MIDDLEWARE TO HANDLE DATABASE CLOSED ERRORS
// ===================================================================

// Track pending operations
let pendingOperations = 0;
const pendingOperations$ = new BehaviorSubject<number>(0);

// Database switching state
export const dbSwitching$ = new BehaviorSubject<boolean>(false);

// Dexie middleware to handle DatabaseClosedError and switching state
const safeOperationMiddleware: Middleware<DBCore> = {
  stack: "dbcore" as const,
  name: "SafeOperationMiddleware",
  create(downlevelDatabase) {
    return {
      ...downlevelDatabase,
      table(tableName) {
        const downlevelTable = downlevelDatabase.table(tableName);
        return {
          ...downlevelTable,
          mutate: async (req) => {
            // Check if database is switching
            if (dbSwitching$.getValue()) {
              console.warn(`Operation on ${tableName} skipped - database is switching`);
              return { numFailures: 0, failures: [], results: [], lastResult: undefined };
            }
            
            pendingOperations++;
            pendingOperations$.next(pendingOperations);
            
            try {
              return await downlevelTable.mutate(req);
            } catch (error: any) {
              if (error?.name === 'DatabaseClosedError') {
                console.warn(`DatabaseClosedError on ${tableName} - returning empty result`);
                return { numFailures: 0, failures: [], results: [], lastResult: undefined };
              }
              throw error;
            } finally {
              pendingOperations--;
              pendingOperations$.next(pendingOperations);
            }
          },
          query: async (req) => {
            // Check if database is switching
            if (dbSwitching$.getValue()) {
              console.warn(`Query on ${tableName} skipped - database is switching`);
              return { result: [] };
            }
            
            pendingOperations++;
            pendingOperations$.next(pendingOperations);
            
            try {
              return await downlevelTable.query(req);
            } catch (error: any) {
              if (error?.name === 'DatabaseClosedError') {
                console.warn(`DatabaseClosedError on ${tableName} - returning empty result`);
                return { result: [] };
              }
              throw error;
            } finally {
              pendingOperations--;
              pendingOperations$.next(pendingOperations);
            }
          },
          openCursor: async (req) => {
            // Check if database is switching
            if (dbSwitching$.getValue()) {
              console.warn(`Cursor on ${tableName} skipped - database is switching`);
              return null as any;
            }
            
            pendingOperations++;
            pendingOperations$.next(pendingOperations);
            
            try {
              return await downlevelTable.openCursor(req);
            } catch (error: any) {
              if (error?.name === 'DatabaseClosedError') {
                console.warn(`DatabaseClosedError on ${tableName} - returning empty cursor`);
                return null as any;
              }
              throw error;
            } finally {
              pendingOperations--;
              pendingOperations$.next(pendingOperations);
            }
          },
        };
      },
    };
  },
};

// Helper to wait for pending operations
async function waitForPendingOperations(maxWaitMs = 3000): Promise<void> {
  const startTime = Date.now();
  while (pendingOperations > 0 && Date.now() - startTime < maxWaitMs) {
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  if (pendingOperations > 0) {
    console.warn(`Still ${pendingOperations} pending operations after ${maxWaitMs}ms wait`);
  }
}

// ===================================================================
//  3. YOUR EXISTING APPLICATION DATABASE
// ===================================================================

@Injectable({
  providedIn: "root",
})
export class AppDB extends Dexie {
  bank: Table;
  bankGroups: Table;
  bankTable: Table;
  customerGroups: Table;
  customers: Table;
  formulaGroups: Table;
  formulas: Table;
  materials: Table;
  production: Table;
  products: Table;
  sells: Table;
  stock: Table;
  stockGroups: Table;
  transactions: Table;
  transactionDeletions: Table;
  users: Table;
  materialSnapshots: Table;
  materialDeltas: Table;
  initialStockBalance: Table;
  reconciliations: Table;

  constructor(tableName: string) {
    super(tableName);
    this.version(21).stores({
      bank: "++id, transactionId, bankGroupId, income, outgoings, cashboxId, tId, date, customerId, tType",
      bankGroups:
        "++id, name, bankEntryType, includeInBalance, bankGroupCategory",
      customerGroups: "++id, name",
      customers: "++id, name, details, customerGroupId",
      formulaGroups: "++id, name, cost, type, formula, latestVersion",
      formulas:
        "++id, formulaGroupId, materialId, productId, includedFormulaGroupId, quantity, version",
      initialStockBalance:
        "++id, materialId, stockGroupId, quantity, price, year",
      materials: "++id, name, price, unitType, currency",
      production:
        "++id, transactionId, productId, quantity, weight, length, productionType, customerId, machineNo, date, dateAdded, formulaGroupId, formulaTotalWeight, tId",
      products:
        "++id, name, code, category, formulaGroupId, weight, uzleme, sellPrice",
      sells:
        "++id, productId, materialId, transactionId, customerId, quantity, price, tId, date, stockGroupId",
      stock:
        "++id, transactionId, stockGroupId, materialId, quantity, price, tId, date, customerId, [materialId+stockGroupId]",
      stockGroups: "++id, name",
      transactions:
        "++id, sum, type, customerId, date, dateAdded, details, status, userId, refersTo",
      transactionDeletions: "++id, transactionId, type",
      users: "++id, username, password, fullName, email, role",
    });
    
    // Version 22: Add snapshot tables for performance optimization
    this.version(22).stores({
      bank: "++id, transactionId, bankGroupId, income, outgoings, cashboxId, tId, date, customerId, tType",
      bankGroups:
        "++id, name, bankEntryType, includeInBalance, bankGroupCategory",
      customerGroups: "++id, name",
      customers: "++id, name, details, customerGroupId",
      formulaGroups: "++id, name, cost, type, formula, latestVersion",
      formulas:
        "++id, formulaGroupId, materialId, productId, includedFormulaGroupId, quantity, version",
      initialStockBalance:
        "++id, materialId, stockGroupId, quantity, price, year",
      materials: "++id, name, price, unitType, currency",
      production:
        "++id, transactionId, productId, quantity, weight, length, productionType, customerId, machineNo, date, dateAdded, formulaGroupId, formulaTotalWeight, tId",
      products:
        "++id, name, code, category, formulaGroupId, weight, uzleme, sellPrice",
      sells:
        "++id, productId, materialId, transactionId, customerId, quantity, price, tId, date, stockGroupId",
      stock:
        "++id, transactionId, stockGroupId, materialId, quantity, price, tId, date, customerId, [materialId+stockGroupId]",
      stockGroups: "++id, name",
      transactions:
        "++id, sum, type, customerId, date, dateAdded, details, status, userId, refersTo",
      transactionDeletions: "++id, transactionId, type",
      users: "++id, username, password, fullName, email, role",
      // NEW: Monthly snapshots for fast balance lookups
      materialSnapshots: "++id, materialId, stockGroupId, yearMonth, openingBalance, closingBalance, [materialId+stockGroupId+yearMonth]",
      // NEW: Delta tracking within each month
      materialDeltas: "++id, materialId, stockGroupId, date, quantityChange, sourceType, sourceId, isProduction, [materialId+stockGroupId+date]"
    });
    
    // Version 23: Update initialStockBalance with compound index for faster lookups
    this.version(23).stores({
      bank: "++id, transactionId, bankGroupId, income, outgoings, cashboxId, tId, date, customerId, tType",
      bankGroups:
        "++id, name, bankEntryType, includeInBalance, bankGroupCategory",
      customerGroups: "++id, name",
      customers: "++id, name, details, customerGroupId",
      formulaGroups: "++id, name, cost, type, formula, latestVersion",
      formulas:
        "++id, formulaGroupId, materialId, productId, includedFormulaGroupId, quantity, version",
      // UPDATED: Better index for initial stock balance lookups
      initialStockBalance:
        "++id, materialId, stockGroupId, quantity, price, year, [materialId+stockGroupId]",
      materials: "++id, name, price, unitType, currency",
      production:
        "++id, transactionId, productId, quantity, weight, length, productionType, customerId, machineNo, date, dateAdded, formulaGroupId, formulaTotalWeight, tId",
      products:
        "++id, name, code, category, formulaGroupId, weight, uzleme, sellPrice",
      sells:
        "++id, productId, materialId, transactionId, customerId, quantity, price, tId, date, stockGroupId",
      stock:
        "++id, transactionId, stockGroupId, materialId, quantity, price, tId, date, customerId, [materialId+stockGroupId]",
      stockGroups: "++id, name",
      transactions:
        "++id, sum, type, customerId, date, dateAdded, details, status, userId, refersTo",
      transactionDeletions: "++id, transactionId, type",
      users: "++id, username, password, fullName, email, role",
      // Monthly snapshots for fast balance lookups
      materialSnapshots: "++id, materialId, stockGroupId, yearMonth, openingBalance, closingBalance, [materialId+stockGroupId+yearMonth]",
      // Delta tracking within each month
      materialDeltas: "++id, materialId, stockGroupId, date, quantityChange, sourceType, sourceId, isProduction, [materialId+stockGroupId+date]"
    });
    
    // Version 24: Add reconciliations table for inventory adjustments (Üzləşmə)
    this.version(24).stores({
      bank: "++id, transactionId, bankGroupId, income, outgoings, cashboxId, tId, date, customerId, tType",
      bankGroups:
        "++id, name, bankEntryType, includeInBalance, bankGroupCategory",
      customerGroups: "++id, name",
      customers: "++id, name, details, customerGroupId",
      formulaGroups: "++id, name, cost, type, formula, latestVersion",
      formulas:
        "++id, formulaGroupId, materialId, productId, includedFormulaGroupId, quantity, version",
      initialStockBalance:
        "++id, materialId, stockGroupId, quantity, price, year, [materialId+stockGroupId]",
      materials: "++id, name, price, unitType, currency",
      production:
        "++id, transactionId, productId, quantity, weight, length, productionType, customerId, machineNo, date, dateAdded, formulaGroupId, formulaTotalWeight, tId",
      products:
        "++id, name, code, category, formulaGroupId, weight, uzleme, sellPrice",
      sells:
        "++id, productId, materialId, transactionId, customerId, quantity, price, tId, date, stockGroupId",
      stock:
        "++id, transactionId, stockGroupId, materialId, quantity, price, tId, date, customerId, [materialId+stockGroupId]",
      stockGroups: "++id, name",
      transactions:
        "++id, sum, type, customerId, date, dateAdded, details, status, userId, refersTo",
      transactionDeletions: "++id, transactionId, type",
      users: "++id, username, password, fullName, email, role",
      materialSnapshots: "++id, materialId, stockGroupId, yearMonth, openingBalance, closingBalance, [materialId+stockGroupId+yearMonth]",
      materialDeltas: "++id, materialId, stockGroupId, date, quantityChange, sourceType, sourceId, isProduction, [materialId+stockGroupId+date]",
      // NEW: Reconciliations table for tracking inventory adjustments
      reconciliations: "++id, transactionId, tId, materialId, stockGroupId, expectedQuantity, actualQuantity, difference, notes, date, [materialId+stockGroupId+date]"
    });
    
    // Apply safe operation wrapper to all tables
    this.use(safeOperationMiddleware);
  }
}

// Your existing logic for the main app database remains the same
export let db = new AppDB(JSON.parse(localStorage.getItem("dbTable")));

// ===================================================================
//  4. DATABASE SWITCHING LOGIC
// ===================================================================

export async function switchDatabase(newDatabaseName: string) {
  if (dbSwitching$.getValue()) {
    console.log("Database switch already in progress, waiting...");
    // Wait for the current switch to complete
    await new Promise<void>((resolve) => {
      const subscription = dbSwitching$.subscribe((switching) => {
        if (!switching) {
          subscription.unsubscribe();
          resolve();
        }
      });
    });
    return db;
  }
  
  dbSwitching$.next(true);
  
  try {
    // Wait for any pending database operations to complete
    console.log(`Waiting for ${pendingOperations} pending operations...`);
    await waitForPendingOperations();
    
    // Close the current database
    if (db.isOpen()) {
      await db.close();
    }
    
    // Create and open new database
    db = new AppDB(newDatabaseName);
    await db.open();
    
    console.log(`Switched to database: ${newDatabaseName}`);
  } catch (error) {
    console.error("Error switching database:", error);
  } finally {
    dbSwitching$.next(false);
  }
  
  return db;
}
